import { Router, UrlTree } from '@angular/router';

const LOGIN_PATH = '/auth/login';
const MAX_RETURN_URL_LENGTH = 2048;

/**
 * Validates a `returnUrl` query param before navigating to it after login.
 *
 * Only same-app absolute paths are accepted, so the param cannot be used as
 * an open redirect: it must start with a single `/` (not `//`, which browsers
 * treat as protocol-relative), contain no backslashes (some browsers
 * normalise `/\` to `//`) and no control characters. Paths under `/auth` are
 * rejected to avoid bouncing back to the login page.
 *
 * Returns the URL unchanged when safe, otherwise null.
 */
export function sanitizeReturnUrl(value: unknown): string | null {
  if (typeof value !== 'string') return null;
  if (value.length === 0 || value.length > MAX_RETURN_URL_LENGTH) return null;
  if (!value.startsWith('/') || value.startsWith('//')) return null;
  if (value.includes('\\')) return null;
  if (/[\u0000-\u001f\u007f]/.test(value)) return null;
  if (value === '/auth' || value.startsWith('/auth/') || value.startsWith('/auth?')) {
    return null;
  }
  return value;
}

/**
 * Login page URL tree, carrying `returnUrl` when the current location is a
 * safe in-app path worth returning to.
 */
export function loginUrlTree(router: Router, returnUrl: string | null | undefined): UrlTree {
  const safe = sanitizeReturnUrl(returnUrl);
  return safe && safe !== '/'
    ? router.createUrlTree([LOGIN_PATH], { queryParams: { returnUrl: safe } })
    : router.createUrlTree([LOGIN_PATH]);
}

/** True when the given router URL is already the login page. */
export function isLoginUrl(url: string): boolean {
  return url === LOGIN_PATH || url.startsWith(`${LOGIN_PATH}?`);
}
